// invest_FE/src/components/inquiries/InquiryCard.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaClock, FaUser, FaEnvelope, FaPhone, FaBuilding, FaChevronDown, FaChevronUp, FaChartLine, FaMoneyBillWave, FaHandshake, FaTachometerAlt, FaCalendarAlt } from 'react-icons/fa';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { apiFetch } from '../../utils/apiClient';
import { BASE_URL } from '../../utils/config';
import { formatDate } from '../../utils/formatting';
import PurchaseModal from '../payment/PurchaseModal';
import './InquiryCard.css';

const CountdownTimer = ({ expiryDate }) => {
  const calculateTimeLeft = useCallback(() => {
    const difference = +new Date(expiryDate) - +new Date();
    let timeLeft = {};

    if (difference > 0) {
      timeLeft = {
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        totalHours: Math.floor(difference / (1000 * 60 * 60)),
      };
    }
    return timeLeft;
  }, [expiryDate]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    // When timeLeft becomes an empty object, the countdown is over.
    if (Object.keys(timeLeft).length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft, calculateTimeLeft]);

  if (Object.keys(timeLeft).length === 0) {
    return <span className="countdown-timer expired">Expired</span>;
  }

  return (
    <span className="countdown-timer">
      <FaClock /> Expires in: {timeLeft.totalHours}h {timeLeft.minutes}m {timeLeft.seconds}s
    </span>
  );
};

const InquiryCard = ({ inquiry, onUpdate, isAdminView, isInCart, forceExpanded = false }) => {
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);

  const [isLoading, setIsLoading] = useState(false); // For decline action
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(forceExpanded); // For unlocked card details

  const toggleExpand = () => {
    if (!forceExpanded) {
      setIsExpanded((prev) => !prev);
    }
  };

  // This now just opens the purchase modal
  const handleUnlock = () => {
    setIsPurchaseModalOpen(true);
  };

  const handleDecline = async () => {
    setIsLoading(true);
    try {
      const response = await apiFetch(`${BASE_URL}/api/inquiries/${inquiry._id}/decline`, { method: 'POST' }, user.token);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to decline inquiry.');
      }
      addNotification('Inquiry declined.', 'info');
      onUpdate();
    } catch (error) {
      addNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchaseSuccess = () => {
    addNotification('Inquiry unlocked successfully!', 'success');
    setIsPurchaseModalOpen(false);
    onUpdate('unlocked'); // Refresh and switch to the 'unlocked' tab
  };

  const renderCardContent = () => {
    const investmentStrategyDisplay = Array.isArray(inquiry.buyer.investmentProfile?.investmentStrategy) ? inquiry.buyer.investmentProfile.investmentStrategy.join(', ') : inquiry.buyer.investmentProfile?.investmentStrategy;
    const financingMethodDisplay = Array.isArray(inquiry.buyer.investmentProfile?.financingMethod) ? inquiry.buyer.investmentProfile.financingMethod.join(', ') : inquiry.buyer.investmentProfile?.financingMethod;

    // Admin view always shows full details in the body
    if (forceExpanded) {
      const visibilityDays = inquiry.visibilityDays || 30;
      return (
        <>
          <div className="contact-asap-notice">
            <strong>Action Recommended:</strong> Contact this buyer as soon as possible to increase your chances of closing a deal.
          </div>
          <div className="visibility-notice">
            <strong>Note:</strong> For your convenience, these details will remain visible here for <strong>{visibilityDays} days</strong> from the unlock date.
          </div>
          <div className="inquiry-message">"{inquiry.message}"</div>
          <div className="buyer-details-grid">
            <div className="grid-col">
              <p>
                <FaUser /> <strong></strong> {inquiry.buyer.firstName} {inquiry.buyer.lastName}
              </p>
              <p>
                <FaEnvelope /> <a href={`mailto:${inquiry.buyer.email}`}>{inquiry.buyer.email}</a>
              </p>
              {inquiry.buyer.phoneNumber && (
                <p>
                  <FaPhone /> {inquiry.buyer.phoneCountryCode} {inquiry.buyer.phoneNumber}
                </p>
              )}
            </div>
            <div className="grid-col">
              <p>
                <FaChartLine /> <strong>Strategy:</strong> {investmentStrategyDisplay || 'N/A'}
              </p>
              <p>
                <FaMoneyBillWave /> <strong>Budget:</strong> {inquiry.buyer.investmentProfile?.investmentBudget || 'N/A'}
              </p>
            </div>
            <div className="grid-col">
              <p>
                <FaHandshake /> <strong>Financing:</strong> {financingMethodDisplay || 'N/A'}
              </p>
              <p>
                <FaTachometerAlt /> <strong>Experience:</strong> {inquiry.buyer.investmentProfile?.experienceLevel || 'N/A'}
              </p>
              <p>
                <FaCalendarAlt /> <strong>Timeline:</strong> {inquiry.buyer.investmentProfile?.purchaseTimeline || 'N/A'}
              </p>
            </div>
          </div>
        </>
      );
    }
    if (isAdminView) {
      return (
        <>
          <div className="inquiry-message">"{inquiry.message}"</div>
          {inquiry.buyer ? (
            <div className="buyer-details">
              <h4>Buyer Information</h4>
              <p>
                <FaUser /> {inquiry.buyer.firstName} {inquiry.buyer.lastName} ({inquiry.buyer.username})
              </p>
              <p>
                <FaEnvelope /> <a href={`mailto:${inquiry.buyer.email}`}>{inquiry.buyer.email}</a>
              </p>
              {inquiry.buyer.phoneNumber && (
                <p>
                  <FaPhone /> {inquiry.buyer.phoneCountryCode} {inquiry.buyer.phoneNumber}
                </p>
              )}
            </div>
          ) : (
            <div className="buyer-details">
              <p>Buyer information is not available (account may have been deleted).</p>
            </div>
          )}
        </>
      );
    }

    // Default view for sellers
    switch (inquiry.status) {
      case 'pending':
        return (
          <>
            <div className="inquiry-message redacted">"{inquiry.message}"</div>
            <div className="inquiry-actions">
              <CountdownTimer expiryDate={inquiry.expiresAt} />
              <div className="action-buttons" title={isInCart ? 'This inquiry is already in your cart.' : ''}>
                <button onClick={handleDecline} className="decline-btn" disabled={isLoading || isInCart}>
                  Decline
                </button>
                <button onClick={handleUnlock} className="unlock-btn" disabled={isLoading || isInCart}>
                  {isInCart ? 'In Cart' : 'Unlock'}
                </button>
              </div>
            </div>
          </>
        );
      case 'unlocked':
        return (
          <>
            {inquiry.isVisibilityExpired ? (
              <div className="visibility-expired-message">For privacy and security, full contact details are hidden after the visibility period has expired.</div>
            ) : (
              <>
                <div className="inquiry-message">"{inquiry.message}"</div>
                {inquiry.buyer ? (
                  <div className="buyer-details-grid">
                    <div className="grid-col">
                      <p>
                        <FaUser /> <strong>Name:</strong> {inquiry.buyer.firstName} {inquiry.buyer.lastName}
                      </p>
                      <p>
                        <FaEnvelope /> <a href={`mailto:${inquiry.buyer.email}`}>{inquiry.buyer.email}</a>
                      </p>
                      {inquiry.buyer.phoneNumber && (
                        <p>
                          <FaPhone /> {inquiry.buyer.phoneCountryCode} {inquiry.buyer.phoneNumber}
                        </p>
                      )}
                    </div>
                    <div className="grid-col">
                      <p>
                        <FaChartLine /> <strong>Strategy:</strong> {investmentStrategyDisplay || 'N/A'}
                      </p>
                      <p>
                        <FaMoneyBillWave /> <strong>Budget:</strong> {inquiry.buyer.investmentProfile?.investmentBudget || 'N/A'}
                      </p>
                    </div>
                    <div className="grid-col">
                      <p>
                        <FaHandshake /> <strong>Financing:</strong> {financingMethodDisplay || 'N/A'}
                      </p>
                      <p>
                        <FaTachometerAlt /> <strong>Experience:</strong> {inquiry.buyer.investmentProfile?.experienceLevel || 'N/A'}
                      </p>
                      <p>
                        <FaCalendarAlt /> <strong>Timeline:</strong> {inquiry.buyer.investmentProfile?.purchaseTimeline || 'N/A'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p>Buyer information is not available.</p>
                )}
              </>
            )}
          </>
        );
      case 'expired':
        return <div className="inquiry-status-overlay">Expired</div>;
      case 'refused':
        return <div className="inquiry-status-overlay">Declined</div>;
      default:
        return null;
    }
  };

  return (
    <>
      <div className={`inquiry-card status-${inquiry.status}`}>
        {inquiry.status === 'unlocked' && !isAdminView && !forceExpanded ? (
          <div className="inquiry-header compact-unlocked-header" onClick={toggleExpand} role="button" tabIndex="0">
            <div className="compact-info-group">
              <div className="compact-buyer-name">
                <FaUser /> {inquiry.buyer?.firstName || 'Unknown'} {inquiry.buyer?.lastName || 'User'}
              </div>
              <div className="compact-address">
                <FaBuilding />{' '}
                {inquiry.property ? (
                  <>
                    {inquiry.property.address}, {inquiry.property.city} (#{inquiry.property.propertyID})
                  </>
                ) : (
                  'Property no longer available'
                )}
              </div>
              <div className="compact-date">
                <FaClock /> {formatDate(inquiry.unlockedAt || inquiry.createdAt)}
              </div>
            </div>
            <div className="expand-indicator">{isExpanded ? <FaChevronUp /> : <FaChevronDown />}</div>
          </div>
        ) : (
          <div className="inquiry-header">
            <div className="property-info">
              <FaBuilding />
              {inquiry.property ? (
                <Link to={`/properties?propertyId=${inquiry.property.propertyID}`} target="_blank" rel="noopener noreferrer">
                  <span className="property-id-header">#{inquiry.property.propertyID}</span> {inquiry.property.address}, {inquiry.property.city}
                </Link>
              ) : (
                <span>Property no longer available</span>
              )}
            </div>
            <div className="inquiry-meta">
              <span className="inquiry-date">Received: {formatDate(inquiry.createdAt, true)}</span>
              <span className={`status-badge status-${inquiry.status}`}>{inquiry.status}</span>
            </div>
          </div>
        )}
        {isAdminView && (
          <div className="admin-info-bar">
            <span className="admin-detail">
              <strong>Buyer:</strong> {inquiry.buyer?.firstName} {inquiry.buyer?.lastName} ({inquiry.buyer?.username || 'N/A'})
            </span>
            <span className="admin-detail">
              <strong>Seller:</strong> {inquiry.seller?.firstName} {inquiry.seller?.lastName} ({inquiry.seller?.username || 'N/A'})
            </span>
          </div>
        )}
        {(inquiry.status !== 'unlocked' || isExpanded) && <div className="inquiry-body">{renderCardContent()}</div>}
      </div>
      {!forceExpanded && <PurchaseModal isOpen={isPurchaseModalOpen} onClose={() => setIsPurchaseModalOpen(false)} purchaseType="inquiry" inquiryId={inquiry._id} onPurchaseSuccess={handlePurchaseSuccess} />}
    </>
  );
};

export default InquiryCard;
